import {
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewEncapsulation,
    Optional,
    Self,
    OnDestroy,
    DoCheck,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { BaseDirective } from '../../base.directive';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'textarea[euiTextArea]',
    styleUrls: ['./styles/_index.scss'],
    template: '<ng-content></ng-content>',
    encapsulation: ViewEncapsulation.None
})
export class EuiTextareaComponent extends BaseDirective implements OnInit, OnDestroy, DoCheck, OnChanges, ControlValueAccessor {

    static idCounter = 0;

    @HostBinding('class')
    public get cssClasses(): any {
        return [
            this.isInvalid ? `eui-textarea__model--invalid` : ``,
            super.getCssClasses('eui-textarea__model'),
        ].join(' ').trim();
    }

    @HostBinding('attr.readonly')
    @Input()
    public get readonly() {
        return this._readonly || undefined;
    }
    public set readonly(state: any) {
        this._readonly = coerceBooleanProperty(state);
    }

    @HostBinding('attr.id')
    @Input() id = `eui-textarea_${EuiTextareaComponent.idCounter++}`;

    @Input() public isInvalid: boolean;

    @HostBinding('attr.data-e2e') @Input() e2eAttr: string = 'eui-textarea';

    protected _readonly: boolean;
    protected hostEl: HTMLTextAreaElement;
    protected hostParentEl: HTMLElement;
    protected hostWrapperEl: HTMLDivElement;
    protected valueContainerEl: HTMLDivElement;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        @Optional() @Self() protected control: NgControl,
        protected _elementRef: ElementRef,
        protected _renderer: Renderer2) {
        super();
    }

    @HostListener('input', ['$event.target.value'])
    public onInputChange(value) {
        this.onChange(value);
    }

    @HostListener('blur', ['$event.target.value'])
    public onBlur(value) {
        this.onTouched(value);
    }

    ngOnInit() {
        this.hostEl = this._elementRef.nativeElement;
        this.hostParentEl = this.hostEl.parentElement;
        this.hostWrapperEl = this.createHostWrapperContainer();
        this.valueContainerEl = this.createValueContainer();

        this.handleMarkup();
        // sets invalid state if control is present
        if (this.control) {
            this.control.valueAccessor = this;
            this.isInvalid = this.control.dirty && this.control.touched && this.control.invalid;
            this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(status => {
                this.isInvalid = status === 'INVALID';
            });
        }
    }

    ngDoCheck() {
        if (this.control) {
            // TODO: Delete when https://github.com/angular/angular/issues/30275 is resolved
            this.isInvalid = this.control.invalid && this.control.touched;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['readonly']) {
            const readonly = changes['readonly'].currentValue;

            if (this.hostWrapperEl && this.valueContainerEl) {
                this.hostWrapperEl.classList.toggle('eui-textarea--readonly', !!readonly);
                this.valueContainerEl.innerText = this.hostEl.value;
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.complete();
        this.destroy$.unsubscribe();
    }

    writeValue(obj: any): void {
        if (this.valueContainerEl) {
            this._renderer.setProperty(this.valueContainerEl, 'innerText', obj);
        }
        this._renderer.setProperty(this._elementRef.nativeElement, 'value', obj);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._renderer.setProperty(this.hostEl, 'disabled', isDisabled);
    }

    protected onChange(_): void {
        this.writeValue(_);
    }

    protected onTouched(_): void {
        if (this.control) {
            this.isInvalid = this.control.invalid;
        }
    }

    private createHostWrapperContainer(): HTMLDivElement {
        const wrapper = this._renderer.createElement('div');
        this._renderer.addClass(wrapper, 'eui-textarea');
        if (this.readonly) {
            this._renderer.addClass(wrapper, 'eui-textarea--readonly');
        } else {
            this._renderer.removeClass(wrapper, 'eui-textarea--readonly');
        }

        return wrapper;
    }

    /**
     * Create a Div element which will hold the value to display when readonly
     * @private
     */
    private createValueContainer(): HTMLDivElement {
        const valueContainer = this._renderer.createElement('div');
        this._renderer.addClass(valueContainer, 'eui-textarea__value-container');
        this._renderer.setProperty(valueContainer, 'innerText', this.hostEl.value.trim().replace( /[\r\n]+/gm, '' ));
        return valueContainer;
    }

    /**
     * Changes the markup around the textarea. Append textarea onto a container with div
     * @private
     */
    private handleMarkup() {
        if (this.hostParentEl != null) {
            this.hostParentEl.replaceChild(this.hostWrapperEl, this.hostEl);
            this.hostWrapperEl.appendChild(this.hostEl);
            this.hostWrapperEl.appendChild(this.valueContainerEl);
        }
    }
}
